import { useTranslation } from "react-i18next";
import {
  Button,
  Center,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList, Tooltip,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  getSortDirection,
  QUESTIONNAIRE_STATUSES,
  showErrorToast,
  showSuccessToast,
  SORT_DIRECTIONS
} from "../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import {
  CSRDKeys,
  useCreateCSRDQuestionnaire, useDeleteCSRDQuestionnaire,
  useGetAllCSRDQuestionnaires, useUnfinaliseCSRDQuestionnaire,
  useUpdateCSRDQuestionnaire
} from "../../../hooks/query/csrd";
import { routes } from "../../../config";
import { MultiSelectFilter } from "../../../components/DataTable/components";
import { FaBars, FaEye, FaPen, FaTrash, FaUndo } from "react-icons/fa";
import { Page } from "../../../components/Page";
import { Card } from "../../../components/Card";
import { DeleteDialog } from "../../DataEntryPlanning/components/DeleteDialog";
import { DataTable } from "../../../components/DataTable";
import { WithAuthorization } from "../../../context";
import { CSRDPlanningModal, ReviewCSRDQuestionnaireModal } from "./components";

function CSRDPlanning() {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    isOpen: isPlanningModalOpen,
    onOpen: onPlanningModalOpen,
    onClose: onPlanningModalClose
  } = useDisclosure();
  const {
    isOpen: isConfirmDialogOpen,
    onOpen: onConfirmDialogOpen,
    onClose: onConfirmDialogClose
  } = useDisclosure();
  const {
    isOpen: isReviewQuestionnaireOpen,
    onOpen: onReviewQuestionnaireOpen,
    onClose: onReviewQuestionnaireClose
  } = useDisclosure();

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [planningModalData, setPlanningModalData] = useState(null);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState("");
  const [tasksList, setTasksList] = useState([]);
  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const {
    totalPages,
    filterObj,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const manageDataTableState = (newValObject) => {
    const calcState = (prevState) => {
      let changedAttributes = {};

      Object.keys(newValObject).forEach(dataTableAttribute => {
        const value = newValObject[dataTableAttribute];

        if (dataTableAttribute === "filterObj") {
          // We have to save not only the new value sent from the data table
          // but also the previously applied filters, which we get from prevState
          changedAttributes[dataTableAttribute] = {
            ...prevState[dataTableAttribute],
            [value.attribute]: value.value
          };
        } else if (dataTableAttribute === "queryPageSortBy") {
          let stringValue = value[0];
          let accessorName = stringValue.split(",")[0];
          const sortDirection = stringValue.split(",")[1];
          const updatedArray = [...prevState[dataTableAttribute]];

          if (accessorName === "assigneeName") {
            // Back-end requires the usage of 'user.email' instead of 'assigneeName' for sorting
            stringValue = `user.email,${sortDirection}`;
            accessorName = "user.email";
          }

          // Removing initial sorting by id, if still available
          const indexOfInitial = updatedArray.findIndex(el => {
            return el.split(",")[0] === "id";
          });
          if (indexOfInitial > -1) {
            updatedArray.splice(indexOfInitial, 1);
          }

          const indexOfExisting = updatedArray.findIndex(el => {
            return el.split(",")[0] === accessorName;
          });
          if (indexOfExisting > -1) {
            // Column which we try to sort is already in the sort list, so we just manipulate its record
            if (sortDirection === SORT_DIRECTIONS.NONE) {
              updatedArray.splice(indexOfExisting, 1);
            } else {
              updatedArray[indexOfExisting] = stringValue;
            }
          } else {
            // Column which we try to sort does not exist in the sort list, so we add it
            updatedArray.push(stringValue);
          }

          changedAttributes[dataTableAttribute] = updatedArray;
        } else {
          // All other values have primitive types, so we apply them directly
          // without checking their previous states
          changedAttributes[dataTableAttribute] = value;
        }
      });

      return changedAttributes;
    };

    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calcState(prevState)
      };
    });
  };

  const handleErrors = (error) => {
    const errorMsg = error.response?.status === 409 &&
    error.response?.data?.parameters?.resourceName === "CsrdQuestionnaire" ?
      globalTranslation("csrd.dataEntryPlanning.duplicatedAssignee") : error.response?.data?.parameters?.message ??
        error.message;

    dispatch(hideAppSpinner());
    showErrorToast(toast, errorMsg);
  };

  const modifyQuestionnaireConfig = {
    onSuccess: () => {
      handlePlanningModalClose();
      showSuccessToast(toast, t("messages.taskCreated"));
      queryClient.invalidateQueries(CSRDKeys.allCSRDQuestionnaires());
      refetchTasks();
    },
      onError: err => handleErrors(err),
      onSettled: () => {
      dispatch(hideAppSpinner());
    }
  };

  const { mutate: create } = useCreateCSRDQuestionnaire(modifyQuestionnaireConfig);
  const { mutate: update } = useUpdateCSRDQuestionnaire(modifyQuestionnaireConfig);

  const { mutate: deleteTask } = useDeleteCSRDQuestionnaire({
    onSuccess: () => {
      onConfirmDialogClose();
      setSelectedRowId(null);
      showSuccessToast(toast, t("messages.taskDeleted"));
      queryClient.invalidateQueries(CSRDKeys.allCSRDQuestionnaires());
      refetchTasks();
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: unfinaliseQuestionnaire } = useUnfinaliseCSRDQuestionnaire({
    onMutate: () => {
      dispatch(showAppSpinner());
    },
    onSuccess: () => {
      showSuccessToast(toast, t("messages.unfinaliseSuccess"));
      queryClient.invalidateQueries(CSRDKeys.allCSRDQuestionnaires());
      refetchTasks();
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const handleAddNewTask = () => {
    setSelectedRowId(null);
    onPlanningModalOpen();
  };

  const handlePlanningModalSubmit = (taskId, dataToSave) => {
    dispatch(showAppSpinner());
    if (taskId) {
      update({ id: taskId, data: dataToSave });
    } else {
      create(dataToSave);
    }
  };

  const columns = useMemo(() => {
      function handleEdit(row) {
        setPlanningModalData(row.row.original);
        onPlanningModalOpen();
      }

      function handleDelete(rowId) {
        setSelectedRowId(rowId);
        onConfirmDialogOpen();
      }

      function handleViewQuestionnaire(recordId) {
        setSelectedQuestionnaireId(recordId);
        onReviewQuestionnaireOpen();
      }

      function handleEditQuestionnaire(questionnaireId) {
        navigate(`${routes.csrdQuestionnaire}?id=${questionnaireId}`);
      }

      function handleUnfinaliseQuestionnaire(questionnaireId) {
        unfinaliseQuestionnaire(questionnaireId);
      }

      return [
        {
          header: globalTranslation("csrd.gridHeaders.questionnaireType"),
          accessorKey: "type",
          size: 150,
          sortDirection: getSortDirection(queryPageSortBy, "type")
        },
        {
          header: globalTranslation("csrd.gridHeaders.assignee"),
          accessorKey: "assigneeName",
          size: 200,
          sortDirection: getSortDirection(queryPageSortBy, "user.email")
        },
        {
          header: globalTranslation("csrd.gridHeaders.year"),
          accessorKey: "year",
          size: 130,
          sortDirection: getSortDirection(queryPageSortBy, "year")
        },
        {
          header: globalTranslation("csrd.gridHeaders.status"),
          accessorKey: "status",
          size: 220,
          sortDirection: getSortDirection(queryPageSortBy, "status"),
          filter: {
            params: {
              options: [
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.FINALIZED}`),
                  value: QUESTIONNAIRE_STATUSES.FINALIZED
                },
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.IN_PROGRESS}`),
                  value: QUESTIONNAIRE_STATUSES.IN_PROGRESS
                },
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.NEW}`),
                  value: QUESTIONNAIRE_STATUSES.NEW
                }
              ],
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          }
        },
        {
          header: <div style={{ textAlign: "left" }}><b>{globalTranslation("csrd.gridHeaders.actions")}</b></div>,
          accessorKey: "action",
          size: 80,
          enableColumnFilter: false,
          isSortable: false,
          cell: row => (
            <Menu isLazy>
              <MenuButton
                as={IconButton}
                aria-label="Actions"
                icon={<FaBars />}
                variant="outline"
              />
              <MenuList>
                <MenuGroup title={t("page.manageTaskGroupHeader")}>
                  <MenuItem
                    icon={<FaPen color="green" opacity="0.7" />}
                    onClick={() => handleEdit(row)}
                    isDisabled={row.row.original.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED}
                  >
                    {t("page.editTask")}
                  </MenuItem>
                  <MenuItem
                    icon={<FaTrash color="red" opacity="0.6" />}
                    onClick={() => handleDelete(row.row.original.id)}
                  >
                    {t("page.deleteTask")}
                  </MenuItem>
                </MenuGroup>
                <MenuDivider />
                <MenuGroup title={t("page.manageDataGroupHeader")}>
                  <MenuItem
                    icon={<FaEye color="green" opacity="0.7" />}
                    onClick={() => handleViewQuestionnaire(row.row.original.id)}
                  >
                    {globalTranslation("csrd.dataEntryPlanning.viewCSRDData")}
                  </MenuItem>
                  <MenuItem
                    icon={<FaPen color="green" opacity="0.7" />}
                    onClick={() => handleEditQuestionnaire(row.row.original.id)}
                    isDisabled={row.row.original.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED}
                    title={row.row.original.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED ?
                      t("page.finalisedQuestionnaireTooltip") : null}
                  >
                    {globalTranslation("csrd.dataEntryPlanning.editCSRDData")}
                  </MenuItem>
                  <MenuItem
                    icon={<FaUndo color="green" opacity="0.7" />}
                    onClick={() => handleUnfinaliseQuestionnaire(row.row.original.id)}
                    isDisabled={row.row.original.originalStatus !== QUESTIONNAIRE_STATUSES.FINALIZED}
                  >
                    {globalTranslation("csrd.dataEntryPlanning.unfinaliseCSRDQuestionnaire")}
                  </MenuItem>
                </MenuGroup>
              </MenuList>
            </Menu>
          )
        }
      ];
    }, [t, globalTranslation, onPlanningModalOpen, onConfirmDialogOpen, onReviewQuestionnaireOpen, navigate,
      unfinaliseQuestionnaire, queryPageSortBy, filterObj]
  );

  const handleDelete = () => {
    dispatch(showAppSpinner());
    deleteTask(selectedRowId);
  };

  const constructQueryParams = () => {
    const queryParams = {
      page: queryPageIndex,
      size: queryPageSize,
      ...filterObj,
      sort: queryPageSortBy
    };
    // "status" and "sort" params are extracted from queryParams object, because they can have more than one value.
    // To avoid overriding of those multiple values which have to share the same key ("status" or "sort"), we put them
    // together in a string called "multipleValuesParams"
    let multipleValuesParams = "";

    Object.keys(queryParams).forEach(param => {
      if (queryParams[param] === "" || queryParams[param].length === 0) {
        // Removing empty params
        delete queryParams[param];
      } else if (param === "status") {
        queryParams[param].forEach(statusObj => {
          multipleValuesParams += `&status=${statusObj.value}`;
        });
        delete queryParams[param];
      } else if (param === "sort") {
        queryParams[param].forEach(sortStr => {
          multipleValuesParams += `&sort=${sortStr}`;
        });
        delete queryParams[param];
      }
    });

    return `${new URLSearchParams(queryParams).toString()}${multipleValuesParams}`;
  };

  const {
    isLoading: isLoadingTasks,
    isFetching: isFetchingTasks,
    refetch: refetchTasks
  } = useGetAllCSRDQuestionnaires(constructQueryParams(),
    {
      onSuccess: (data) => {
        const pageContent = data?.content ?? [];
        const tasks = pageContent.map((task) => {
          return {
            id: task?.id,
            type: globalTranslation(`csrd.questionnaireTypes.${task?.type ?? ""}`, { defaultValue: "" }),
            originalType: task?.type,
            assigneeName: task?.assignee ?? "",
            assignee: task?.assignee ?? "",
            year: task?.year?.toString() ?? "",
            status: t("status." + task?.status),
            originalStatus: task?.status
          };
        });
        setTasksList(tasks);
        if (pageContent.length === 0 && data?.number !== 0 && data?.totalElements !== 0 && queryPageIndex > 0) {
          // Case when the last record on last page is deleted and user has to be redirected to the previous page
          manageDataTableState({
            totalElements: data?.totalElements ?? 0,
            totalPages: data?.totalPages ?? 0,
            queryPageIndex: queryPageIndex - 1
          });
        } else {
          manageDataTableState({
            totalElements: data?.totalElements ?? 0,
            totalPages: data?.totalPages ?? 0
          });
        }
      },
      onError: err => handleErrors(err), cacheTime: 0,
      onSettled: () => {
        dispatch(hideAppSpinner());
      }
    }
  );

  const handlePlanningModalClose = () => {
    onPlanningModalClose();
    setPlanningModalData(null);
  };

  useEffect(() => {
    refetchTasks();
  }, [refetchTasks, queryPageIndex, queryPageSize]);

  const closeReviewQuestionnaireModal = () => {
    setSelectedQuestionnaireId("");
    onReviewQuestionnaireClose();
  };

  return (
    <Page
      title={globalTranslation("csrd.dataEntryPlanning.page.title")}
      subTitle={globalTranslation("csrd.dataEntryPlanning.page.subTitle")}
    >
      <Card>
        <Center marginBlockEnd="5">
          <Tooltip label={t("page.addNewTask.tooltip")}>
            <Button
              colorScheme="customScheme"
              width="40"
              onClick={handleAddNewTask}>
              + {t("page.addNewTask.title")}
            </Button>
          </Tooltip>
        </Center>

        {isPlanningModalOpen && <CSRDPlanningModal
          modalData={planningModalData}
          isOpen={isPlanningModalOpen}
          onClose={handlePlanningModalClose}
          onSubmit={handlePlanningModalSubmit}
        />}

        <DeleteDialog
          isOpen={isConfirmDialogOpen}
          onClose={onConfirmDialogClose}
          onDelete={handleDelete}
          entityName={"Task"}
        />

        <ReviewCSRDQuestionnaireModal
          questionnaireId={selectedQuestionnaireId}
          isOpen={isReviewQuestionnaireOpen}
          onClose={closeReviewQuestionnaireModal}
        />

        <DataTable
          data={tasksList}
          columns={columns}
          isLoading={isLoadingTasks || isFetchingTasks}
          serverSidePagination={true}
          queryTotalCount={totalElements}
          queryPageCount={totalPages}
          queryPageIndex={queryPageIndex}
          setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
          queryPageSize={queryPageSize}
          setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
          queryPageSortBy={queryPageSortBy}
          setQueryPageSortBy={(value) => manageDataTableState({ queryPageSortBy: value })}
          filterObj={filterObj}
          setFilterObj={(newFilterObj) => {
            manageDataTableState({
              filterObj: newFilterObj,
              queryPageIndex: 0
            });
          }}
          enableFilters={true}
          enableSorting={true}
          containerHeight="var(--chakra-sizes-2xl)"
        />
      </Card>
    </Page>
  );
}

export default WithAuthorization(CSRDPlanning);